/**
 * Restyle trigger/poll/fetch endpoints (spec §4.5 rows 5-7, epic pirx-2gm7r,
 * Tasks 7 + 10). Thin wrappers over the `/api/audits/:auditId/restyle*`
 * routes. Uses the shared `request()` helper which attaches the active-team
 * header (see `services/api/request.ts`), so callers don't need to thread
 * the team id through. Task 8 (pirx-2gm7r.8) layers an entitlement gate on
 * top of these routes in the backend; the standard `ApiError` envelope covers
 * 403/503 (team/flag not entitled) and 402 (insufficient credit balance), so
 * callers can branch on `err.status` / `err.code` without coordinating with
 * Task 8's exact wire shape.
 */

import { request } from "./request";

export type RestyleJobStatus =
  | "queued"
  | "processing"
  | "completed"
  | "failed";

export interface TriggerRestyleResult {
  jobId: string;
  usedFreeTrial: boolean;
}

export interface RestyleJobStatusResult {
  status: RestyleJobStatus;
  claimsTotal: number;
  claimsCompleted: number;
  documentCompleted: boolean;
}

export interface StyledClaimVariant {
  claimId: string;
  styledSaferPhrasing: string;
}

export interface StyledOutputResult {
  claims: StyledClaimVariant[];
  document: string | null;
}

/**
 * Trigger a restyle job for an audit. The backend returns a `jobId` and a
 * `usedFreeTrial` flag (Decision 9's billing status). On 402 the backend
 * throws `InsufficientBalanceError` (propagates as `ApiError` with status
 * 402 and code `INSUFFICIENT_BALANCE`); on 403/503 it throws the flag/team
 * gate errors (codes `FORBIDDEN` / `SERVICE_UNAVAILABLE`).
 */
export async function triggerRestyle(
  auditId: string,
  styleId: string,
): Promise<TriggerRestyleResult> {
  return request<TriggerRestyleResult>(
    `/audits/${auditId}/restyle`,
    {
      method: "POST",
      body: { styleId },
      contentType: "json",
    },
  );
}

/**
 * Poll the restyle job. Stops polling client-side once status is `completed`
 * or `failed` (the spec §4.3 step 6 atomicity rule: a failed job offers no
 * styled output, so the picker does not surface it as ready).
 */
export async function getRestyleJob(
  auditId: string,
  jobId: string,
): Promise<RestyleJobStatusResult> {
  return request<RestyleJobStatusResult>(
    `/audits/${auditId}/restyle/${jobId}`,
  );
}

/**
 * Fetch the per-claim `styledSaferPhrasing` and the whole-document
 * `styledDocumentText` for a `(auditId, styleId)` pair. The backend only
 * serves these for a `completed`, non-`stale` job (404 otherwise).
 */
export async function getStyledOutput(
  auditId: string,
  styleId: string,
): Promise<StyledOutputResult> {
  const search = new URLSearchParams({ styleId });
  return request<StyledOutputResult>(
    `/audits/${auditId}/styled-output?${search.toString()}`,
  );
}